import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import { toast } from '../utils/toast';
import { TrendingUp, TrendingDown, Star } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const StaffProfilePage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [profileData, setProfileData] = useState(null);
  const [editing, setEditing] = useState(false);
  const [formData, setFormData] = useState({ name: '', email: '', phone: '' });
  const [completedTopics, setCompletedTopics] = useState([]);
  const [loadingTopics, setLoadingTopics] = useState(true);
  
  useEffect(() => {
    fetchProfile();
    fetchCompletedTopics();
  }, []);
  
  const fetchProfile = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/staff/profile`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProfileData(response.data);
      setFormData({
        name: response.data.profile.name,
        email: response.data.profile.email,
        phone: response.data.profile.phone
      });
    } catch (error) {
      toast.error('Failed to fetch profile');
    } finally {
      setLoading(false);
    }
  };
  
  const fetchCompletedTopics = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/staff/profile/completed-topics`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCompletedTopics(response.data.completed_topics || []);
    } catch (error) {
      console.error('Failed to fetch completed topics:', error);
      toast.error('Failed to fetch completed topics');
    } finally {
      setLoadingTopics(false);
    }
  };
  
  const handleSave = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.put(
        `${API}/staff/profile/update`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` },
          params: formData
        }
      );
      toast.success('Profile updated successfully');
      localStorage.setItem('user', JSON.stringify(response.data.user));
      setEditing(false);
      fetchProfile();
    } catch (error) {
      toast.error('Failed to update profile');
    }
  };
  
  if (loading || !profileData) {
    return <div className="min-h-screen bg-[#F5F6FA] flex items-center justify-center"><div className="w-16 h-16 border-4 border-[#0056B3] border-t-transparent rounded-full animate-spin"></div></div>;
  }
  
  return (
    <div className="min-h-screen bg-[#F5F6FA]">
      <header className="bg-white border-b border-gray-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <h1 className="text-[22px] font-semibold" style={{ color: '#0056B3' }}>My Profile</h1>
            <Button onClick={() => navigate('/staff/dashboard')} variant="outline">Back</Button>
          </div>
        </div>
      </header>
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-8">
        <Card className="p-6">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-[18px] font-bold" style={{ color: '#0056B3' }}>Personal Information</h2>
            {!editing && <Button onClick={() => setEditing(true)}>Edit Profile</Button>}
          </div>
          {editing ? (
            <div className="space-y-4">
              <div><Label>Name</Label><Input value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} /></div>
              <div><Label>Email</Label><Input type="email" value={formData.email} onChange={(e) => setFormData({...formData, email: e.target.value})} /></div>
              <div><Label>Phone</Label><Input value={formData.phone} onChange={(e) => setFormData({...formData, phone: e.target.value})} /></div>
              <div className="flex space-x-2">
                <Button onClick={() => setEditing(false)} variant="outline" className="flex-1">Cancel</Button>
                <Button onClick={handleSave} className="flex-1 bg-[#0056B3]">Save</Button>
              </div>
            </div>
          ) : (
            <div className="space-y-3">
              <div><Label className="text-gray-600">Name</Label><p className="font-medium">{profileData.profile.name}</p></div>
              <div><Label className="text-gray-600">Email</Label><p className="font-medium">{profileData.profile.email}</p></div>
              <div><Label className="text-gray-600">Phone</Label><p className="font-medium">{profileData.profile.phone || 'Not provided'}</p></div>
              <div><Label className="text-gray-600">Department</Label><p className="font-medium">{profileData.profile.department}</p></div>
              <div><Label className="text-gray-600">Job Role</Label><p className="font-medium">{profileData.profile.job_function}</p></div>
            </div>
          )}
        </Card>
        <Card className="p-6">
          <h2 className="text-[18px] font-bold mb-6" style={{ color: '#0056B3' }}>Training Metrics</h2>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <div className="text-center"><p className="text-[22px] font-bold" style={{ color: '#0056B3' }}>{profileData.metrics.total_topics}</p><p className="text-sm text-gray-600">Total</p></div>
            <div className="text-center"><p className="text-[22px] font-bold text-blue-600">{profileData.metrics.visited}</p><p className="text-sm text-gray-600">Visited</p></div>
            <div className="text-center"><p className="text-[22px] font-bold text-green-600">{profileData.metrics.completed}</p><p className="text-sm text-gray-600">Completed</p></div>
            <div className="text-center"><p className="text-[22px] font-bold text-yellow-600">{profileData.metrics.in_progress}</p><p className="text-sm text-gray-600">In Progress</p></div>
            <div className="text-center"><p className="text-[22px] font-bold text-gray-600">{profileData.metrics.untapped}</p><p className="text-sm text-gray-600">Untapped</p></div>
          </div>
        </Card>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Card className="p-6">
            <div className="flex items-center space-x-3 mb-4">
              <TrendingUp className="w-6 h-6 text-green-600" />
              <h2 className="text-lg font-bold text-green-600">Areas of Strength</h2>
            </div>
            <p className="text-sm text-gray-600 mb-4">Chapters with ≥{profileData.performance.strength_threshold}% score</p>
            {profileData.performance.strengths.length === 0 ? (
              <p className="text-gray-500">No strengths identified yet</p>
            ) : (
              <div className="flex flex-wrap gap-2">
                {profileData.performance.strengths.map(s => (
                  <div 
                    key={s.training_topic_id} 
                    className="px-4 py-2 bg-green-50 border border-green-200 rounded-lg hover:bg-green-100 transition-colors"
                  >
                    <span className={s.score === 100 ? "font-bold text-gray-900" : "text-gray-900"}>
                      {s.training_topic_name}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </Card>
          <Card className="p-6">
            <div className="flex items-center space-x-3 mb-4">
              <TrendingDown className="w-6 h-6 text-orange-600" />
              <h2 className="text-lg font-bold text-orange-600">Areas for Improvement</h2>
            </div>
            <p className="text-sm text-gray-600 mb-4">Chapters with ≤{profileData.performance.improvement_threshold}% score</p>
            {profileData.performance.improvements.length === 0 ? (
              <p className="text-gray-500">No improvement areas identified</p>
            ) : (
              <div className="flex flex-wrap gap-2">
                {profileData.performance.improvements.map(i => (
                  <div 
                    key={i.training_topic_id} 
                    className="px-4 py-2 bg-orange-50 border border-orange-200 rounded-lg hover:bg-orange-100 transition-colors"
                  >
                    <span className="text-gray-900">{i.training_topic_name}</span>
                  </div>
                ))}
              </div>
            )}
          </Card>
        </div>

        {/* Completed Chapters Table */}
        <Card className="p-6">
          <h2 className="text-[18px] font-bold mb-6" style={{ color: '#0056B3' }}>My Completed Chapters</h2>
          {loadingTopics ? (
            <div className="flex justify-center py-8">
              <div className="w-8 h-8 border-4 border-[#0056B3] border-t-transparent rounded-full animate-spin"></div>
            </div>
          ) : completedTopics.length === 0 ? (
            <p className="text-gray-500 text-center py-8">No completed chapters yet. Keep learning!</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="text-left py-3 px-4 font-semibold text-gray-700">Chapter Name</th>
                    <th className="text-center py-3 px-4 font-semibold text-gray-700">Score (%)</th>
                    <th className="text-center py-3 px-4 font-semibold text-gray-700">Completed On</th>
                    <th className="text-center py-3 px-4 font-semibold text-gray-700">Average Feedback Given</th>
                  </tr>
                </thead>
                <tbody>
                  {completedTopics.map((topic, index) => (
                    <tr key={topic.topic_id} className={`border-b border-gray-100 hover:bg-gray-50 transition-colors ${index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`}>
                      <td className="py-3 px-4 font-medium text-gray-900">{topic.topic_name}</td>
                      <td className="py-3 px-4 text-center">
                        {topic.score !== null && topic.score !== undefined ? (
                          <span className="inline-block px-3 py-1 bg-green-100 text-green-700 rounded-full font-semibold">
                            {topic.score}%
                          </span>
                        ) : (
                          <span className="text-gray-500">No assessment</span>
                        )}
                      </td>
                      <td className="py-3 px-4 text-center text-gray-600">
                        {topic.completed_at ? new Date(topic.completed_at).toLocaleDateString('en-US', { 
                          year: 'numeric', 
                          month: 'short', 
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        }) : '–'}
                      </td>
                      <td className="py-3 px-4 text-center">
                        {topic.average_feedback ? (
                          <div className="flex items-center justify-center space-x-1">
                            {[1, 2, 3, 4, 5].map((star) => (
                              <Star
                                key={star}
                                className={`w-4 h-4 ${
                                  star <= Math.round(topic.average_feedback)
                                    ? 'fill-yellow-400 text-yellow-400'
                                    : 'text-gray-300'
                                }`}
                              />
                            ))}
                            <span className="ml-2 text-sm text-gray-600">({topic.average_feedback})</span>
                          </div>
                        ) : (
                          <span className="text-gray-500">No feedback</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      </main>
    </div>
  );
};

export default StaffProfilePage;